import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FiBarChart2,
  FiDownload,
  FiFileText,
  FiPlus,
  FiSave,
  FiTrash2,
  FiUploadCloud,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import InlineLoader from './InlineLoader';
import { apiUrl } from '../utils/api';
import { buildSafeUploadFileName } from '../utils/fileNames';

const LD_OPTIONS = [
  { value: 50, label: 'LD50' },
  { value: 90, label: 'LD90' },
  { value: 95, label: 'LD95' },
  { value: 99, label: 'LD99' },
];

const MODEL_OPTIONS = [
  { value: 'probit', label: 'Probit' },
  { value: 'logit', label: 'Logit' },
];

const INPUT_MODE_OPTIONS = [
  { value: 'file', label: 'Subir TXT', description: 'Usa un archivo crudo ya preparado.' },
  { value: 'manual', label: 'Carga manual', description: 'Completa grupos y filas para generar el TXT crudo.' },
];

const INITIAL_MANUAL_METADATA_LINES = ['', ''];
const MANUAL_RAW_PLACEHOLDER = `# El TXT crudo se generara aqui cuando completes al menos una fila valida.
# Cada linea de metadatos se exporta con "=" y cada grupo con "*".`;

const createClientId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
const buildDefaultGroupLabel = (index) => String(index + 1).padStart(3, '0');

const createManualRow = (overrides = {}) => ({
  id: createClientId(),
  dose: '',
  total: '',
  dead: '',
  ...overrides,
});

const createManualGroup = (index = 0, overrides = {}) => ({
  id: createClientId(),
  label: buildDefaultGroupLabel(index),
  rows: Array.isArray(overrides.rows) && overrides.rows.length ? overrides.rows : [createManualRow()],
  ...overrides,
});

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Token ${token}` } : {};
};

const formatMetric = (value, digits = 4) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return 'No disponible';
  }

  return Number(value).toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

const decodeBase64ToFile = (base64Value, fileName, mimeType) => {
  const binary = window.atob(base64Value);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], fileName, { type: mimeType });
};

const downloadBase64File = ({ base64Value, fileName, mimeType }) => {
  const file = decodeBase64ToFile(base64Value, fileName, mimeType);
  const objectUrl = window.URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

const downloadBlobFile = ({ blob, fileName }) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

const buildAnalysisNameFromSelection = ({ selectedSample, selectedProtocol }) => {
  if (selectedSample?.protocolo_muestra) {
    return `Dosis-respuesta ${selectedSample.protocolo_muestra}`;
  }
  if (selectedProtocol) {
    return `Dosis-respuesta ${selectedProtocol}`;
  }
  return 'An\u00e1lisis dosis-respuesta';
};

const buildManualFileStem = ({ analysisName, selectedSample, selectedProtocol }) => {
  const explicitName = String(analysisName || '').trim();
  if (explicitName) {
    return explicitName;
  }
  if (selectedSample?.protocolo_muestra) {
    return `dosis-respuesta-${selectedSample.protocolo_muestra}`;
  }
  if (selectedProtocol) {
    return `dosis-respuesta-${selectedProtocol}`;
  }
  return 'crudo';
};

const normalizeManualDose = (value) => {
  const normalizedValue = String(value ?? '').trim().replace(',', '.');
  if (!normalizedValue) {
    return '';
  }
  const numericValue = Number(normalizedValue);
  if (Number.isNaN(numericValue) || numericValue < 0) {
    return null;
  }
  return normalizedValue;
};

const rowHasAnyValue = (row) => ['dose', 'total', 'dead'].some((field) => String(row?.[field] ?? '').trim());
const rowIsComplete = (row) => ['dose', 'total', 'dead'].every((field) => String(row?.[field] ?? '').trim());
const isPositiveIntegerString = (value) => /^\d+$/.test(String(value ?? '').trim()) && Number(String(value ?? '').trim()) > 0;
const isNonNegativeIntegerString = (value) => /^\d+$/.test(String(value ?? '').trim());

const buildManualRawTextPayload = ({ metadataLines, groups, fallbackFileStem = 'crudo', strict = true }) => {
  const fileName = buildSafeUploadFileName(`${fallbackFileStem}-crudo.txt`);

  try {
    const normalizedMetadata = metadataLines
      .map((line) => String(line ?? '').trim())
      .filter(Boolean)
      .map((line) => `=${line}`);
    const groupBlocks = [];
    let validRowsCount = 0;

    groups.forEach((group, groupIndex) => {
      const groupLabel = String(group?.label ?? '').trim() || buildDefaultGroupLabel(groupIndex);
      const rowLines = [];

      (group?.rows || []).forEach((row, rowIndex) => {
        if (!rowHasAnyValue(row)) {
          return;
        }
        if (!rowIsComplete(row)) {
          if (strict) {
            throw new Error(`Completa o elimina la fila ${rowIndex + 1} del grupo ${groupLabel}.`);
          }
          return;
        }
        const normalizedDose = normalizeManualDose(row.dose);
        if (normalizedDose === null) {
          if (strict) {
            throw new Error(`La dosis de la fila ${rowIndex + 1} del grupo ${groupLabel} no es numerica.`);
          }
          return;
        }
        if (!isPositiveIntegerString(row.total)) {
          if (strict) {
            throw new Error(`El valor n de la fila ${rowIndex + 1} del grupo ${groupLabel} debe ser un entero mayor a cero.`);
          }
          return;
        }
        if (!isNonNegativeIntegerString(row.dead)) {
          if (strict) {
            throw new Error(`El valor de muertos de la fila ${rowIndex + 1} del grupo ${groupLabel} debe ser un entero mayor o igual a cero.`);
          }
          return;
        }
        const total = Number(String(row.total).trim());
        const dead = Number(String(row.dead).trim());
        if (dead > total) {
          if (strict) {
            throw new Error(`La fila ${rowIndex + 1} del grupo ${groupLabel} no puede tener muertos mayores que n.`);
          }
          return;
        }
        rowLines.push(`${normalizedDose} ${total} ${dead}`);
        validRowsCount += 1;
      });

      if (rowLines.length > 0) {
        groupBlocks.push(`*${groupLabel}\n${rowLines.join('\n')}`);
      } else if (strict) {
        throw new Error(`El grupo ${groupLabel} no tiene filas completas.`);
      }
    });

    if (validRowsCount === 0) {
      return { fileName, rawText: '', hasRows: false, error: strict ? 'Agrega al menos una fila valida para generar el TXT crudo.' : null };
    }

    return {
      fileName,
      rawText: `${[...(normalizedMetadata.length ? [normalizedMetadata.join('\n')] : []), ...groupBlocks].join('\n\n')}\n`,
      hasRows: true,
      error: null,
    };
  } catch (error) {
    return { fileName, rawText: '', hasRows: false, error: error.message || 'No se pudo generar el TXT crudo.' };
  }
};
const ParasitologiaDoseResponseForm = ({
  protocoloGeneralInicial = '',
  protocoloMuestraInicial = '',
  returnToCaseDetail = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [protocolos, setProtocolos] = useState([]);
  const [muestras, setMuestras] = useState([]);
  const [protocoloSeleccionado, setProtocoloSeleccionado] = useState(protocoloGeneralInicial);
  const [protocoloMuestra, setProtocoloMuestra] = useState(protocoloMuestraInicial);
  const [analysisName, setAnalysisName] = useState('');
  const [selectedModel, setSelectedModel] = useState('probit');
  const [selectedLdLevels, setSelectedLdLevels] = useState([50, 90, 95, 99]);
  const [inputMode, setInputMode] = useState('file');
  const [selectedFile, setSelectedFile] = useState(null);
  const [manualMetadataLines, setManualMetadataLines] = useState(INITIAL_MANUAL_METADATA_LINES);
  const [manualGroups, setManualGroups] = useState(() => [createManualGroup(0)]);
  const [loadingProtocols, setLoadingProtocols] = useState(true);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [processingPreview, setProcessingPreview] = useState(false);
  const [savingResult, setSavingResult] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [resultText, setResultText] = useState('');
  const [chartRanges, setChartRanges] = useState({
    curveYMin: '-0.02',
    curveYMax: '1.02',
    residualYMin: '-5',
    residualYMax: '5',
  });

  const clearPreviewState = () => {
    setPreviewData(null);
    setResultText('');
    setSaveNotice(null);
  };

  useEffect(() => {
    let active = true;
    const fetchProtocolos = async () => {
      setLoadingProtocols(true);
      try {
        const response = await axios.get(apiUrl('/api/ingresos_casos/?compact=1'));
        if (active) {
          setProtocolos(response.data || []);
        }
      } catch (error) {
        if (active) {
          toast.error('No se pudieron cargar los protocolos.');
          console.error('Detalles del error:', error.response?.data || error);
        }
      } finally {
        if (active) {
          setLoadingProtocols(false);
        }
      }
    };
    fetchProtocolos();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!protocoloSeleccionado) {
      setMuestras([]);
      setProtocoloMuestra('');
      return;
    }

    let active = true;
    const fetchMuestras = async () => {
      setLoadingSamples(true);
      try {
        const response = await axios.get(
          apiUrl(`/api/ingresos_muestras/?protocolo_general=${encodeURIComponent(protocoloSeleccionado)}`)
        );
        if (active) {
          setMuestras(response.data || []);
        }
      } catch (error) {
        if (active) {
          setMuestras([]);
          toast.error('No se pudieron cargar las muestras del protocolo.');
          console.error('Detalles del error:', error.response?.data || error);
        }
      } finally {
        if (active) {
          setLoadingSamples(false);
        }
      }
    };
    fetchMuestras();
    return () => {
      active = false;
    };
  }, [protocoloSeleccionado]);

  const muestrasParasitologia = useMemo(
    () => muestras.filter((muestra) => muestra.protocolo_general === protocoloSeleccionado && muestra.sector === 'PA'),
    [muestras, protocoloSeleccionado]
  );

  const muestraSeleccionada = useMemo(
    () => muestrasParasitologia.find((muestra) => muestra.protocolo_muestra === protocoloMuestra) || null,
    [muestrasParasitologia, protocoloMuestra]
  );

  const manualFileStem = useMemo(
    () => buildManualFileStem({ analysisName, selectedSample: muestraSeleccionada, selectedProtocol: protocoloSeleccionado }),
    [analysisName, muestraSeleccionada, protocoloSeleccionado]
  );

  const manualRawPreviewPayload = useMemo(
    () => buildManualRawTextPayload({
      metadataLines: manualMetadataLines,
      groups: manualGroups,
      fallbackFileStem: manualFileStem,
      strict: false,
    }),
    [manualMetadataLines, manualGroups, manualFileStem]
  );

  const manualSummary = useMemo(() => {
    let groupsWithData = 0;
    let completeRows = 0;

    manualGroups.forEach((group) => {
      let groupHasData = false;
      (group.rows || []).forEach((row) => {
        if (!rowIsComplete(row)) {
          return;
        }
        const normalizedDose = normalizeManualDose(row.dose);
        if (normalizedDose === null || !isPositiveIntegerString(row.total) || !isNonNegativeIntegerString(row.dead)) {
          return;
        }
        if (Number(String(row.dead).trim()) > Number(String(row.total).trim())) {
          return;
        }
        completeRows += 1;
        groupHasData = true;
      });
      if (groupHasData) {
        groupsWithData += 1;
      }
    });

    return { groupsWithData, completeRows };
  }, [manualGroups]);

  useEffect(() => {
    if (!muestrasParasitologia.length) {
      setProtocoloMuestra('');
      return;
    }
    if (protocoloMuestraInicial) {
      const initialSample = muestrasParasitologia.find((muestra) => muestra.protocolo_muestra === protocoloMuestraInicial);
      if (initialSample) {
        setProtocoloMuestra(initialSample.protocolo_muestra);
        return;
      }
    }
    if (!muestrasParasitologia.some((muestra) => muestra.protocolo_muestra === protocoloMuestra)) {
      setProtocoloMuestra(muestrasParasitologia[0].protocolo_muestra);
    }
  }, [muestrasParasitologia, protocoloMuestra, protocoloMuestraInicial]);

  useEffect(() => {
    clearPreviewState();
  }, [protocoloSeleccionado, protocoloMuestra, selectedModel]);

  const toggleLdLevel = (ldValue) => {
    setSelectedLdLevels((current) => {
      if (current.includes(ldValue)) {
        const nextValues = current.filter((value) => value !== ldValue);
        return nextValues.length > 0 ? nextValues : current;
      }
      return [...current, ldValue].sort((left, right) => left - right);
    });
    clearPreviewState();
  };

  const handleInputModeChange = (nextMode) => {
    if (nextMode === inputMode) {
      return;
    }
    setInputMode(nextMode);
    if (nextMode === 'manual') {
      setSelectedFile(null);
    }
    clearPreviewState();
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files?.[0] || null);
    clearPreviewState();
  };

  const handleChartRangeChange = (field, value) => {
    setChartRanges((current) => ({ ...current, [field]: value }));
    clearPreviewState();
  };

  const handleAnalysisNameChange = (value) => {
    setAnalysisName(value);
    clearPreviewState();
  };

  const handleManualMetadataLineChange = (index, value) => {
    setManualMetadataLines((current) => current.map((line, lineIndex) => (lineIndex === index ? value : line)));
    clearPreviewState();
  };

  const handleAddMetadataLine = () => {
    setManualMetadataLines((current) => [...current, '']);
    clearPreviewState();
  };

  const handleRemoveMetadataLine = (index) => {
    setManualMetadataLines((current) => (current.length === 1 ? [''] : current.filter((_, lineIndex) => lineIndex !== index)));
    clearPreviewState();
  };

  const handleManualGroupLabelChange = (groupId, value) => {
    setManualGroups((current) => current.map((group) => (group.id === groupId ? { ...group, label: value } : group)));
    clearPreviewState();
  };

  const handleManualRowChange = (groupId, rowId, field, value) => {
    setManualGroups((current) => current.map((group) => (
      group.id === groupId
        ? { ...group, rows: group.rows.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)) }
        : group
    )));
    clearPreviewState();
  };
  const handleAddManualRow = (groupId) => {
    setManualGroups((current) => current.map((group) => (
      group.id === groupId ? { ...group, rows: [...group.rows, createManualRow()] } : group
    )));
    clearPreviewState();
  };

  const handleRemoveManualRow = (groupId, rowId) => {
    setManualGroups((current) => current.map((group) => {
      if (group.id !== groupId) {
        return group;
      }
      if (group.rows.length === 1) {
        return { ...group, rows: [createManualRow()] };
      }
      return { ...group, rows: group.rows.filter((row) => row.id !== rowId) };
    }));
    clearPreviewState();
  };

  const handleAddManualGroup = () => {
    setManualGroups((current) => [...current, createManualGroup(current.length)]);
    clearPreviewState();
  };

  const handleRemoveManualGroup = (groupId) => {
    setManualGroups((current) => (current.length === 1 ? [createManualGroup(0)] : current.filter((group) => group.id !== groupId)));
    clearPreviewState();
  };

  const buildSourceDataFile = () => {
    if (inputMode === 'manual') {
      const manualPayload = buildManualRawTextPayload({
        metadataLines: manualMetadataLines,
        groups: manualGroups,
        fallbackFileStem: manualFileStem,
        strict: true,
      });

      if (manualPayload.error) {
        return { file: null, error: manualPayload.error };
      }

      return {
        file: new File([manualPayload.rawText], manualPayload.fileName, { type: 'text/plain;charset=utf-8' }),
        error: null,
      };
    }

    if (!selectedFile) {
      return { file: null, error: 'Adjunta un archivo TXT o utiliza la carga manual.' };
    }

    return { file: selectedFile, error: null };
  };

  const handleDownloadGeneratedRawText = () => {
    const payload = buildManualRawTextPayload({
      metadataLines: manualMetadataLines,
      groups: manualGroups,
      fallbackFileStem: manualFileStem,
      strict: true,
    });
    if (payload.error) {
      toast.error(payload.error);
      return;
    }
    downloadBlobFile({ blob: new Blob([payload.rawText], { type: 'text/plain;charset=utf-8' }), fileName: payload.fileName });
    toast.success('Se descargo el TXT crudo generado.');
  };

  const handleProcessPreview = async (event) => {
    event.preventDefault();
    setSaveNotice(null);

    if (!protocoloSeleccionado) {
      toast.error('Selecciona un protocolo general.');
      return;
    }
    if (!protocoloMuestra) {
      toast.error('Selecciona una muestra de Parasitolog\u00eda.');
      return;
    }
    if (selectedLdLevels.length === 0) {
      toast.error('Selecciona al menos un nivel LD.');
      return;
    }

    const { file: sourceFile, error: sourceFileError } = buildSourceDataFile();
    if (sourceFileError) {
      toast.error(sourceFileError);
      return;
    }

    const formData = new FormData();
    formData.append('archivo_datos', sourceFile, buildSafeUploadFileName(sourceFile.name));
    formData.append('modelo', selectedModel);
    formData.append('nombre_analisis', analysisName || buildAnalysisNameFromSelection({
      selectedSample: muestraSeleccionada,
      selectedProtocol: protocoloSeleccionado,
    }));
    selectedLdLevels.forEach((level) => formData.append('niveles_ld', String(level)));
    formData.append('curve_y_min', chartRanges.curveYMin);
    formData.append('curve_y_max', chartRanges.curveYMax);
    formData.append('residual_y_min', chartRanges.residualYMin);
    formData.append('residual_y_max', chartRanges.residualYMax);

    setProcessingPreview(true);
    try {
      const response = await axios.post(apiUrl('/api/parasitologia/dosis-respuesta/'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAuthHeaders(),
        },
      });
      setPreviewData(response.data);
      setResultText(response.data?.resultado_texto_sugerido || '');
      setSaveNotice({
        type: 'success',
        text: 'Se gener\u00f3 la previsualizaci\u00f3n con el resumen, los gr\u00e1ficos y el informe descargable.',
      });
      toast.success('Previsualizaci\u00f3n generada correctamente.');
    } catch (error) {
      const message = error.response?.data?.detail || 'No se pudieron procesar los datos. Revisa el TXT crudo e intenta nuevamente.';
      setPreviewData(null);
      setResultText('');
      setSaveNotice({ type: 'error', text: message });
      toast.error(message);
      console.error('Detalles del error:', error.response?.data || error);
    } finally {
      setProcessingPreview(false);
    }
  };

  const handleDownloadReport = () => {
    if (!previewData?.report?.base64) {
      toast.error('Genera primero una previsualizaci\u00f3n.');
      return;
    }
    downloadBase64File({
      base64Value: previewData.report.base64,
      fileName: previewData.report.file_name,
      mimeType: previewData.report.mime_type,
    });
  };

  const handleSaveResult = async () => {
    setSaveNotice(null);

    if (!muestraSeleccionada) {
      toast.error('Selecciona una muestra de Parasitolog\u00eda.');
      return;
    }
    if (!previewData?.report?.base64) {
      toast.error('Genera primero una previsualizaci\u00f3n antes de guardar.');
      return;
    }
    if (!resultText.trim()) {
      toast.error('El resumen del resultado no puede quedar vac\u00edo.');
      return;
    }

    setSavingResult(true);
    try {
      const { file: sourceFile, error: sourceFileError } = buildSourceDataFile();
      if (sourceFileError) {
        toast.error(sourceFileError);
        return;
      }

      const generatedReport = decodeBase64ToFile(
        previewData.report.base64,
        previewData.report.file_name,
        previewData.report.mime_type
      );
      const resolvedAnalysisName = previewData.analysis_name || analysisName || buildAnalysisNameFromSelection({
        selectedSample: muestraSeleccionada,
        selectedProtocol: protocoloSeleccionado,
      });
      const formData = new FormData();
      formData.append('muestra', muestraSeleccionada.protocolo_muestra);
      formData.append('sector', muestraSeleccionada.sector || 'PA');
      formData.append('tipo_analisis', previewData.tipo_analisis_sugerido || 'Calculadora Dosis-Respuesta');
      formData.append('resultado_texto', resultText.trim());
      formData.append('archivo_resultado', generatedReport, buildSafeUploadFileName(previewData.report.file_name));
      formData.append('archivo_datos_crudos', sourceFile, buildSafeUploadFileName(sourceFile.name));
      formData.append('modo_datos_crudos', inputMode);
      formData.append('nombre_analisis_datos_crudos', resolvedAnalysisName);

      const response = await axios.post(apiUrl('/api/resultados/'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAuthHeaders(),
        },
      });

      const successMessage = 'El resultado de Parasitolog\u00eda se guard\u00f3 con el informe adjunto y el TXT crudo asociado.';
      setSaveNotice({ type: 'success', text: successMessage });
      toast.success(successMessage);

      if (returnToCaseDetail || location.state?.returnToCaseDetail) {
        navigate(`/ver-caso/${protocoloSeleccionado}`, {
          state: {
            muestraActiva: muestraSeleccionada.protocolo_muestra,
            resultadoActivo: response.data?.id || '',
            resultadoRecienCreado: response.data?.id || '',
          },
        });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'No se pudo guardar el resultado diagn\u00f3stico con el informe generado.';
      setSaveNotice({ type: 'error', text: message });
      toast.error(message);
      console.error('Detalles del error:', error.response?.data || error);
    } finally {
      setSavingResult(false);
    }
  };

  const groupedCharts = useMemo(() => {
    const chartMap = {};
    (previewData?.charts || []).forEach((chart) => {
      if (!chartMap[chart.group_label]) {
        chartMap[chart.group_label] = [];
      }
      chartMap[chart.group_label].push(chart);
    });
    return Object.entries(chartMap);
  }, [previewData]);

  if (loadingProtocols) {
    return (
      <div className="surface-card">
        <InlineLoader label={'Cargando protocolos para Parasitolog\u00eda...'} />
      </div>
    );
  }
  return (
    <div className="surface-card">
      <div className="form-highlight mb-4">
        <div>
          <span className="section-kicker">Sector PA</span>
          <h3 className="form-title">Calculadora Dosis-Respuesta</h3>
          <p className="page-subtitle mt-2 mb-0">
            {'Sube el TXT crudo o carga los datos manualmente, ajusta el modelo probit o logit y genera un informe con resultados y gr\u00e1ficos listo para guardar.'}
          </p>
        </div>
        <div className="protocol-chip">
          {protocoloMuestra ? `Muestra ${protocoloMuestra}` : 'Selecciona una muestra'}
        </div>
      </div>

      <div className="result-form-grid">
        <div className="result-form-grid__main">
          <form onSubmit={handleProcessPreview}>
            {saveNotice ? (
              <div className={`alert ${saveNotice.type === 'error' ? 'subtle-alert' : 'alert-success'} mb-4`} role="status">
                {saveNotice.text}
              </div>
            ) : null}

            <div className="row g-3">
              <div className="col-12 col-lg-6">
                <label className="form-label">Protocolo general</label>
                <select className="form-select" value={protocoloSeleccionado} onChange={(event) => setProtocoloSeleccionado(event.target.value)} required>
                  <option value="">Seleccione un protocolo...</option>
                  {protocolos.map((protocolo) => (
                    <option key={protocolo.protocolo_general} value={protocolo.protocolo_general}>
                      {protocolo.protocolo_general}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-lg-6">
                <label className="form-label">{'Muestra de Parasitolog\u00eda'}</label>
                <select
                  className="form-select"
                  value={protocoloMuestra}
                  onChange={(event) => setProtocoloMuestra(event.target.value)}
                  disabled={!protocoloSeleccionado || loadingSamples}
                  required
                >
                  <option value="">{protocoloSeleccionado ? 'Seleccione una muestra PA...' : 'Primero elige un protocolo'}</option>
                  {muestrasParasitologia.map((muestra) => (
                    <option key={muestra.protocolo_muestra} value={muestra.protocolo_muestra}>
                      {muestra.protocolo_muestra}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12">
                {loadingSamples ? <InlineLoader label="Cargando muestras del protocolo..." /> : null}
                {protocoloSeleccionado && !loadingSamples && muestrasParasitologia.length === 0 ? (
                  <div className="alert subtle-alert d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-0">
                    <div>
                      {'El protocolo '}<strong>{protocoloSeleccionado}</strong>{' no tiene muestras del sector Parasitolog\u00eda.'}
                    </div>
                    <Link to="/ingreso-muestra" state={{ protocoloGeneral: protocoloSeleccionado, returnToCaseDetail: true }} className="btn btn-outline-brand">
                      Agregar muestra
                    </Link>
                  </div>
                ) : null}
              </div>

              <div className="col-12 col-lg-7">
                <label className="form-label">{'Nombre del an\u00e1lisis'}</label>
                <input
                  type="text"
                  className="form-control"
                  value={analysisName}
                  onChange={(event) => handleAnalysisNameChange(event.target.value)}
                  placeholder={buildAnalysisNameFromSelection({ selectedSample: muestraSeleccionada, selectedProtocol: protocoloSeleccionado })}
                />
                <div className="form-text mt-2">{'Si lo dejas vac\u00edo, se arma autom\u00e1ticamente a partir del protocolo y la muestra elegida.'}</div>
              </div>

              <div className="col-12 col-lg-5">
                <label className="form-label">Modelo del ajuste</label>
                <select className="form-select" value={selectedModel} onChange={(event) => setSelectedModel(event.target.value)}>
                  {MODEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="col-12">
                <label className="form-label">Niveles LD a calcular</label>
                <div className="dose-response-ld-grid">
                  {LD_OPTIONS.map((option) => (
                    <label key={option.value} className="dose-response-ld-option">
                      <input type="checkbox" checked={selectedLdLevels.includes(option.value)} onChange={() => toggleLdLevel(option.value)} />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="col-12">
                <label className="form-label">{'Rango de los gr\u00e1ficos'}</label>
                <div className="row g-3">
                  <div className="col-12 col-md-6 col-xl-3">
                    <label className="form-label form-label--subtle">{'Curva Y m\u00ednimo'}</label>
                    <input type="number" step="any" className="form-control" value={chartRanges.curveYMin} onChange={(event) => handleChartRangeChange('curveYMin', event.target.value)} placeholder="-0.02" />
                  </div>
                  <div className="col-12 col-md-6 col-xl-3">
                    <label className="form-label form-label--subtle">{'Curva Y m\u00e1ximo'}</label>
                    <input type="number" step="any" className="form-control" value={chartRanges.curveYMax} onChange={(event) => handleChartRangeChange('curveYMax', event.target.value)} placeholder="1.02" />
                  </div>
                  <div className="col-12 col-md-6 col-xl-3">
                    <label className="form-label form-label--subtle">{'Residuo Y m\u00ednimo'}</label>
                    <input type="number" step="any" className="form-control" value={chartRanges.residualYMin} onChange={(event) => handleChartRangeChange('residualYMin', event.target.value)} placeholder="-5" />
                  </div>
                  <div className="col-12 col-md-6 col-xl-3">
                    <label className="form-label form-label--subtle">{'Residuo Y m\u00e1ximo'}</label>
                    <input type="number" step="any" className="form-control" value={chartRanges.residualYMax} onChange={(event) => handleChartRangeChange('residualYMax', event.target.value)} placeholder="5" />
                  </div>
                </div>
                <div className="form-text mt-2">{'Puedes ajustar el rango antes de generar la previsualizaci\u00f3n. Por ejemplo, residuos de -4 a 4.'}</div>
              </div>

              <div className="col-12">
                <label className="form-label">Modo de carga</label>
                <div className="dose-response-mode-switch">
                  {INPUT_MODE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`dose-response-mode-switch__button ${inputMode === option.value ? 'dose-response-mode-switch__button--active' : ''}`}
                      onClick={() => handleInputModeChange(option.value)}
                    >
                      <strong>{option.label}</strong>
                      <small>{option.description}</small>
                    </button>
                  ))}
                </div>
              </div>

              {inputMode === 'file' ? (
                <>
                  <div className="col-12">
                    <label className="form-label">Archivo TXT</label>
                    <div className="dose-response-upload">
                      <input id="dose-response-file" type="file" className="dose-response-upload__input" accept=".txt,text/plain" onChange={handleFileChange} />
                      <label htmlFor="dose-response-file" className="dose-response-upload__button">
                        <span className="dose-response-upload__icon"><FiUploadCloud /></span>
                        <span>
                          <strong>{selectedFile ? 'Cambiar archivo TXT' : 'Seleccionar archivo TXT'}</strong>
                          <small>{selectedFile?.name || 'Carga un archivo con el formato del crudo.'}</small>
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="dose-response-format-card">
                      <span className="section-kicker">Formato esperado</span>
                      <pre className="dose-response-format-card__code">
{`=Nombre del antiparasitario
=Referencia o ensayo
*001
0   103   1
0.1 104   2
1.5 189 145`}
                      </pre>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="col-12">
                    <div className="dose-response-manual-card">
                      <div className="dose-response-manual-card__header">
                        <div>
                          <span className="section-kicker">Metadatos</span>
                          <h5 className="dose-response-manual-card__title">Encabezado del TXT crudo</h5>
                          <p className="dose-response-manual-card__text">Cada linea se exporta con = al inicio.</p>
                        </div>
                        <button type="button" className="btn btn-outline-brand" onClick={handleAddMetadataLine}>
                          <FiPlus /> Agregar linea
                        </button>
                      </div>

                      <div className="dose-response-manual-meta-list">
                        {manualMetadataLines.map((line, index) => (
                          <div key={`meta-${index + 1}`} className="dose-response-manual-meta-row">
                            <input type="text" className="form-control" value={line} onChange={(event) => handleManualMetadataLineChange(index, event.target.value)} placeholder={index === 0 ? 'Nombre del antiparasitario' : 'Referencia, comentario o condicion'} />
                            <button type="button" className="btn btn-outline-danger" onClick={() => handleRemoveMetadataLine(index)} aria-label={`Quitar linea de metadato ${index + 1}`}>
                              <FiTrash2 />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="dose-response-manual-card">
                      <div className="dose-response-manual-card__header">
                        <div>
                          <span className="section-kicker">Datos</span>
                          <h5 className="dose-response-manual-card__title">Grupos y filas de dosis-respuesta</h5>
                          <p className="dose-response-manual-card__text">Completa dosis, n y muertos para generar el TXT crudo.</p>
                        </div>
                        <button type="button" className="btn btn-outline-brand" onClick={handleAddManualGroup}>
                          <FiPlus /> Agregar grupo
                        </button>
                      </div>

                      <div className="dose-response-group-list">
                        {manualGroups.map((group, groupIndex) => (
                          <section key={group.id} className="dose-response-group-card">
                            <div className="dose-response-group-card__header">
                              <div className="dose-response-group-card__title-wrap">
                                <label className="form-label form-label--subtle">Etiqueta del grupo</label>
                                <input type="text" className="form-control" value={group.label} onChange={(event) => handleManualGroupLabelChange(group.id, event.target.value)} placeholder={buildDefaultGroupLabel(groupIndex)} />
                              </div>
                              <button type="button" className="btn btn-outline-danger" onClick={() => handleRemoveManualGroup(group.id)} aria-label={`Quitar grupo ${groupIndex + 1}`}>
                                <FiTrash2 /> Quitar grupo
                              </button>
                            </div>

                            <div className="dose-response-group-table">
                              <div className="dose-response-group-row dose-response-group-row--header">
                                <span>Dosis</span>
                                <span>n</span>
                                <span>Muertos</span>
                                <span>Acciones</span>
                              </div>
                              {group.rows.map((row, rowIndex) => (
                                <div key={row.id} className="dose-response-group-row">
                                  <input type="text" className="form-control" value={row.dose} onChange={(event) => handleManualRowChange(group.id, row.id, 'dose', event.target.value)} placeholder={rowIndex === 0 ? '0' : '0.1'} />
                                  <input type="text" inputMode="numeric" className="form-control" value={row.total} onChange={(event) => handleManualRowChange(group.id, row.id, 'total', event.target.value)} placeholder="100" />
                                  <input type="text" inputMode="numeric" className="form-control" value={row.dead} onChange={(event) => handleManualRowChange(group.id, row.id, 'dead', event.target.value)} placeholder="0" />
                                  <button type="button" className="btn btn-outline-danger" onClick={() => handleRemoveManualRow(group.id, row.id)} aria-label={`Quitar fila ${rowIndex + 1} del grupo ${group.label || buildDefaultGroupLabel(groupIndex)}`}>
                                    <FiTrash2 />
                                  </button>
                                </div>
                              ))}
                            </div>

                            <div className="dose-response-group-actions">
                              <button type="button" className="btn btn-outline-brand" onClick={() => handleAddManualRow(group.id)}>
                                <FiPlus /> Agregar fila
                              </button>
                            </div>
                          </section>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="dose-response-format-card">
                      <div className="dose-response-format-card__top">
                        <div>
                          <span className="section-kicker">TXT generado</span>
                          <p className="dose-response-manual-card__text mb-0">El archivo se genera automaticamente y se envia al backend al procesar.</p>
                        </div>
                        <button type="button" className="btn btn-outline-brand" onClick={handleDownloadGeneratedRawText} disabled={!manualRawPreviewPayload.hasRows}>
                          <FiDownload /> Descargar TXT crudo
                        </button>
                      </div>
                      <pre className="dose-response-raw-preview">{manualRawPreviewPayload.rawText || MANUAL_RAW_PLACEHOLDER}</pre>
                      <div className="form-text mt-2">
                        {manualRawPreviewPayload.hasRows ? `Se exportara como ${manualRawPreviewPayload.fileName}.` : 'Completa al menos una fila valida para habilitar la descarga del TXT crudo.'}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 pt-4">
              <button type="submit" className="btn btn-brand" disabled={processingPreview || loadingSamples}>
                {processingPreview ? 'Procesando datos...' : 'Procesar datos y generar previsualizacion'}
              </button>

              <div className="d-flex flex-column flex-md-row gap-2">
                <button type="button" className="btn btn-outline-brand" onClick={handleDownloadReport} disabled={!previewData?.report?.base64}>
                  <FiDownload /> Descargar informe
                </button>
                <button type="button" className="btn btn-outline-brand" onClick={handleSaveResult} disabled={savingResult || !previewData?.report?.base64}>
                  <FiSave /> {savingResult ? 'Guardando...' : 'Guardar resultado'}
                </button>
              </div>
            </div>
          </form>

          {previewData ? (
            <section className="dose-response-preview mt-4">
              <div className="dose-response-preview__header">
                <div>
                  <span className="section-kicker">{'Previsualizaci\u00f3n'}</span>
                  <h4 className="result-summary-card__title mb-1">{previewData.analysis_name}</h4>
                  <p className="result-summary-card__text mb-0">{'El resumen se guardar\u00e1 como resultado diagn\u00f3stico y el informe DOCX quedar\u00e1 adjunto autom\u00e1ticamente.'}</p>
                </div>
                <div className="dose-response-preview__chips">
                  <span className="protocol-chip"><FiFileText /> {previewData.tipo_analisis_sugerido}</span>
                  <span className="protocol-chip"><FiBarChart2 /> {previewData.model_name}</span>
                </div>
              </div>

              <div className="mb-4">
                <label className="form-label">{'Resumen que se guardar\u00e1 en el resultado'}</label>
                <textarea className="form-control result-form__textarea" value={resultText} onChange={(event) => setResultText(event.target.value)} rows="12" />
              </div>

              <div className="dose-response-summary-grid">
                {(previewData.group_results || []).map((groupResult) => (
                  <article key={groupResult.group_label} className="dose-response-summary-card">
                    <h5>{groupResult.group_label}</h5>
                    <dl>
                      <div><dt>Control</dt><dd>{formatMetric(groupResult.control_response * 100, 2)}%</dd></div>
                      <div><dt>Intercepto</dt><dd>{formatMetric(groupResult.intercept)}</dd></div>
                      <div><dt>Pendiente</dt><dd>{formatMetric(groupResult.slope)}</dd></div>
                      <div><dt>Deviance</dt><dd>{formatMetric(groupResult.deviance)}</dd></div>
                      <div><dt>Chi Pearson</dt><dd>{formatMetric(groupResult.pearson_chi2)}</dd></div>
                      <div><dt>Heterogeneidad</dt><dd>{formatMetric(groupResult.heterogeneity)}</dd></div>
                    </dl>
                    <div className="dose-response-summary-card__lds">
                      {groupResult.ld_results?.map((ldResult) => (
                        <span key={`${groupResult.group_label}-${ldResult.label}`} className="protocol-chip">
                          {ldResult.label}: {formatMetric(ldResult.dose)}
                        </span>
                      ))}
                    </div>
                  </article>
                ))}
              </div>

              {groupedCharts.map(([groupLabel, charts]) => (
                <section key={groupLabel} className="dose-response-chart-block">
                  <div className="dose-response-chart-block__header"><h5>{`Gr\u00e1ficos del grupo ${groupLabel}`}</h5></div>
                  <div className="dose-response-chart-grid">
                    {charts.map((chart) => (
                      <article key={`${chart.group_label}-${chart.chart_type}`} className="dose-response-chart-card">
                        <img src={chart.data_url} alt={chart.title} className="dose-response-chart-card__image" />
                        <div className="dose-response-chart-card__caption">{chart.title}</div>
                      </article>
                    ))}
                  </div>
                </section>
              ))}
            </section>
          ) : null}
        </div>

        <aside className="result-form-grid__aside">
          <div className="result-summary-card">
            <span className="section-kicker">Resumen</span>
            <h4 className="result-summary-card__title">Antes de guardar</h4>
            <p className="result-summary-card__text">{'Esta pantalla est\u00e1 pensada para muestras del sector PA y deja el informe completo adjunto al resultado diagn\u00f3stico.'}</p>
            <dl className="result-summary-list">
              <div><dt>Protocolo general</dt><dd>{protocoloSeleccionado || 'Sin seleccionar'}</dd></div>
              <div><dt>Muestra</dt><dd>{protocoloMuestra || 'Sin seleccionar'}</dd></div>
              <div><dt>Modelo</dt><dd>{selectedModel}</dd></div>
              <div><dt>LD activas</dt><dd>{selectedLdLevels.map((value) => `LD${value}`).join(', ')}</dd></div>
              <div><dt>Origen de datos</dt><dd>{inputMode === 'manual' ? 'Carga manual' : selectedFile?.name || 'Archivo TXT'}</dd></div>
              <div><dt>Residuos Y</dt><dd>{`${chartRanges.residualYMin || '-5'} a ${chartRanges.residualYMax || '5'}`}</dd></div>
              {inputMode === 'manual' ? <div><dt>Filas manuales</dt><dd>{`${manualSummary.completeRows} filas validas en ${manualSummary.groupsWithData} grupo(s)`}</dd></div> : null}
            </dl>

            {muestraSeleccionada ? (
              <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
                La muestra <strong>{muestraSeleccionada.protocolo_muestra}</strong> pertenece al caso <strong>{muestraSeleccionada.protocolo_general}</strong>.
              </div>
            ) : null}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ParasitologiaDoseResponseForm;
